import { Error, Lock } from '@mui/icons-material';
import { FormControl, InputLabel, Select, MenuItem, FormHelperText, InputAdornment } from '@mui/material';
import { useCallback, useContext } from 'react';
import { useFormState, Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { SelectableItem } from 'src/shared/models/gui/selectableItem';
import { Colors } from '../styles/colors';
import { FormSelectableContext } from './formSelectableStateContext';

const ControlledSelectBox = (props) => {
  const { control } = useFormContext();
  const { errors } = useFormState();
  const { t } = useTranslation(['common']);

  const { updateItems } = useContext(FormSelectableContext);

  const {
    items,
    label,
    name,
    disabled,
    required,
    helpertext,
    defaultValue,
    // ...rest
  } = props;

  let isError = false;
  let errorMessage = '';

  if (errors && Object.prototype.hasOwnProperty.call(errors, name)) {
    isError = true;
    errorMessage = errors[name]?.message?.toString() || '';
  }

  const onChangeCallback = useCallback(
    (item: SelectableItem) => {
      if (!(typeof updateItems === undefined)) {
        updateItems?.({ name, selectableItem: item });
      } else {
        ConsoleHelper.error('updateItems is not defined, use provider');
      }
    },
    [name, updateItems],
  );

  const labelId = `select-label-${Date.now().toString(36) + Math.random().toString(36).substring(2)}`;

  const noMenuItems = items.length === 0;

  return (
    <Controller
      name={name}
      defaultValue={defaultValue ?? ''}
      control={control}
      render={({ field }) => (
        <FormControl
          required={required}
          sx={{
            '& .MuiInputLabel-formControl': {
              backgroundColor: 'white',
              px: '5px',
            },
            '.MuiInputBase-root': {
              backgroundColor: disabled ? Colors.Form.DisabledInput : null,
            },
            '& .MuiSelect-icon': {
              display: disabled ? 'none' : 'inline-block',
            },
          }}
          disabled={!items || disabled}
          fullWidth
          error={isError}
        >
          <InputLabel shrink id={labelId}>
            {label}
          </InputLabel>
          <Select
            {...props}
            {...field}
            displayEmpty
            disabled={noMenuItems || disabled}
            style={{
              cursor: noMenuItems ? 'not-allowed !important' : null,
            }}
            {...((isError || disabled) && {
              endAdornment: (
                <InputAdornment position='end'>
                  {isError && <Error sx={{ color: 'error.main' }} />}
                  {disabled && <Lock sx={{ color: 'text.disabled' }} />}
                </InputAdornment>
              ),
            })}
            value={field.value}
            labelId={labelId}
            variant='outlined'
            size='small'
            aria-invalid={isError}
            aria-required={required}
            onChange={(event) => {
              onChangeCallback(
                items.find((c: SelectableItem) => {
                  return c.value === event.target.value;
                }),
              );
              field.onChange(event.target.value);
            }}
          >
            {items && noMenuItems && (
              <MenuItem value='' disabled>
                {t('common:statusMessages.loading')}
              </MenuItem>
            )}
            {items &&
              items.map((item) => {
                return (
                  <MenuItem value={item.value} key={item.value}>
                    {item.label}
                  </MenuItem>
                );
              })}
          </Select>
          {isError && (
            <FormHelperText>
              <span>{errorMessage}</span>
            </FormHelperText>
          )}
          {helpertext && <FormHelperText>{helpertext}</FormHelperText>}
        </FormControl>
      )}
    />
  );
};

export default ControlledSelectBox;
